import React, { useContext, useState, useMemo, useEffect } from 'react';
import useConversionFactor from '../hooks/useConversionFactor';
import { LanguageContext } from "../config/multilingualContext";
import MultiLingualContent from "../config/multilingualContent";
import UnitRadioGroup from "./UnitRadioGroup";
import ExportDropdown from './ExportDropdown';
import FormattedNumber from './FormattedNumber';
import { getLabel } from "../functions/lang";

const Simpbalance = ({ configsColumn, tableStatData, configsLine, configsUnit, allUnits }) => {
    const { language } = useContext(LanguageContext);
    const [selected, setSelected] = useState(null);

    // initialize selected from available units
    useEffect(() => {
        if (selected == null) {
            if (configsUnit && (configsUnit.id || configsUnit.value)) {
                setSelected(String(configsUnit.id ?? configsUnit.value));
            } else {
                setSelected(String(7)); // fallback
            }
        }
    }, [allUnits, configsUnit]);

    // unit conversion
    const fromUnit = configsUnit?.id ?? (allUnits && allUnits[0]?.id) ?? 7;
    const toUnit = selected;
    const { factor, loading } = useConversionFactor(fromUnit, toUnit);

    // derived table with factor applied
    const displayTable = useMemo(() => {
        if (!tableStatData) return tableStatData;
        const out = {};
        Object.keys(tableStatData).forEach(lineId => {
            out[lineId] = {};
            const cols = tableStatData[lineId];
            Object.keys(cols).forEach(colId => {
                const raw = cols[colId];
                const n = Number(raw);
                out[lineId][colId] = isFinite(n) ? n * Number(factor) : raw;
            });
        });
        return out;
    }, [tableStatData, factor]);

    // mapping row IDs to labels
    const rowLabels = useMemo(() => {
        const map = {};
        if (configsLine?.length) {
            configsLine.forEach(item => {
                map[item.fk_data_id] = item.label_fr ?? item.label_en ?? item.label_pr ?? item.label_sw ?? item.fk_data_id;
            });
        }
        return map;
    }, [configsLine]);

    const colors = ['#f2ce61', '#fff2cc', '#94b8da', '#c6e0b4'];
    let colorIndex = -1;

    return (
        <div style={{ width: '100%' }}>
            {displayTable && Object.keys(displayTable).length > 0 &&
                <>
                    <UnitRadioGroup allUnits={allUnits} selected={selected} onChange={setSelected} />
                    <ExportDropdown
                        tableData={displayTable}
                        columns={configsColumn}
                        filenamePrefix={`simpbalance-${selected || 'unit'}`}
                        rowLabels={rowLabels}
                        configsLine={configsLine}
                        configsUnit={configsUnit}
                        language={language}
                        showUnitRow={false}
                        showTotal={true}
                        activeUnitLabel={(allUnits && selected)
                            ? allUnits.find(u => String(u.id) === String(selected))?.label_fr || ''
                            : configsUnit?.label_fr || configsUnit?.label_en || ''
                        }
                        showUnitInHeaderLabels={false}
                    />
                </>
            }

            {loading && (
                <div style={{ display: 'inline-block', marginLeft: 12 }}>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span className="ms-2">Loading...</span>
                </div>
            )}

            <div style={{ overflowY: 'scroll', height: '100vh' }}>
                {displayTable && Object.keys(displayTable).length > 0 ? (
                    <table className="table table-bordered stripe display mt-2" style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th className="fixed-td"></th>
                                {configsColumn?.map((item, idx) => (
                                    <th className="scrollable-td fixed-hd" key={idx}>
                                        {getLabel({
                                            label_fr: item.label_fr,
                                            label_en: item.label_en,
                                            label_pr: item.label_pr,
                                            label_sw: item.label_sw
                                        }, language)}
                                    </th>
                                ))}
                                <th>TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {configsLine?.map((item, idx) => {
                                let total = 0;
                                if (item.level === 1) colorIndex++;

                                return (
                                    <tr key={idx} style={{ backgroundColor: colors[colorIndex % colors.length] }}>
                                        <td className={`fixed-td ${item.level === 1 ? 'fw-bold px-2' : item.level === 2 ? 'px-4' : 'px-5'}`}>
                                            {getLabel({
                                                label_fr: item.label_fr,
                                                label_en: item.label_en,
                                                label_pr: item.label_pr,
                                                label_sw: item.label_sw
                                            }, language)}
                                        </td>
                                        {configsColumn.map((col, cidx) => {
                                            const rawVal = displayTable[item.fk_data_id]?.[col.id];
                                            const val = rawVal != null ? Number(rawVal) : null;
                                            total += val || 0;
                                            return (
                                                <td className="scrollable-td" key={cidx}>
                                                    {val != null ? <FormattedNumber value={val} locale={language} decimals={2} /> : '-'}
                                                </td>
                                            );
                                        })}
                                        <td>{total !== 0 ? <FormattedNumber value={total} locale={language} decimals={2} /> : '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <p><MultiLingualContent contentID="nodata" /></p>
                )}
            </div>
        </div>
    );
};

export default Simpbalance;